import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  Avatar,
  Paper
} from '@mui/material';
import {
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();

  const menuItems = [
    {
      title: 'Customer Management',
      description: 'Add, edit, and manage customer information',
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: 'primary.main',
      path: '/customers',
      permission: 'customers.view'
    },
    {
      title: 'Transaction Entry',
      description: 'Record daily gas transactions',
      icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
      color: 'success.main',
      path: '/transactions',
      permission: 'transactions.view'
    },
    {
      title: 'Passbook Printing',
      description: 'Generate and print customer passbooks',
      icon: <PrintIcon sx={{ fontSize: 40 }} />,
      color: 'info.main',
      path: '/passbook',
      permission: 'passbook.print'
    }
  ];

  const availableMenuItems = menuItems.filter(item => 
    !item.permission || hasPermission(item.permission)
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Welcome Section */}
        <Paper elevation={3} sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'white' }}>
            <Avatar sx={{ mr: 3, bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
              {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                Welcome, {user?.firstName} {user?.lastName}!
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                {user?.tenantName} - Gas Passbook Management System
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Menu Cards */}
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          Quick Access
        </Typography>
        
        <Grid container spacing={3}>
          {availableMenuItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={6} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => navigate(item.path)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      mr: 2, 
                      p: 1.5, 
                      borderRadius: 2, 
                      backgroundColor: `${item.color}15`,
                      color: item.color 
                    }}>
                      {item.icon}
                    </Box>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                      {item.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {item.description}
                  </Typography>
                  <Button 
                    variant="contained" 
                    fullWidth
                    sx={{ 
                      textTransform: 'none',
                      backgroundColor: item.color,
                      '&:hover': {
                        backgroundColor: item.color,
                        filter: 'brightness(0.9)'
                      }
                    }}
                  >
                    Access {item.title}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* User Info Section */}
        <Paper elevation={1} sx={{ mt: 4, p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Account Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Email:</strong> {user?.email}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Organization:</strong> {user?.tenantName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Roles:</strong> {user?.roles?.join(', ') || 'None'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Permissions:</strong> {user?.permissions?.length || 0} granted
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default Dashboard;